import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { exchangeCodeForToken, forceLocalLogout } from "./auth";
import { useDispatch } from "react-redux";
import { setAuth } from "../authSlice";
import AppLoader from "../../../assets/images/appLoader.gif";
const AuthCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        const code = searchParams.get("code");
        const domainParam = searchParams.get("domain");

        // Always send plain hostname (no protocol/path) as domain
        const rawDomain = domainParam ? decodeURIComponent(domainParam) : window.location.hostname;
        const domain = rawDomain.replace(/^https?:\/\//i, "").split("/")[0];

        const redirectUri = `${window.location.origin}/auth/callback`;
        if (code) {
            exchangeCodeForToken(code, domain, redirectUri).then(async (data) => {
                if (!data) {
                    // Any login error should clear local storage and send the user back to the public site
                    // so that subsequent login attempts use the domain-specific loginUrl from the header API.
                    const redirectDomain = searchParams.get("domain");
                    const target = redirectDomain
                        ? `/?domain=${encodeURIComponent(redirectDomain)}`
                        : "/";
                    forceLocalLogout({ redirectTo: target });
                    return;
                }

                if (data) {
                    // Support both { token: {...} } and flat shape { accessToken, refreshToken, expiresIn, ... }
                    const tokenPayload: any = (data as any).token ?? data;

                    const contact: any = (data as any).contact ?? (data as any).account ?? null;

                    let authData: any = {
                        accessToken: tokenPayload.accessToken,
                        refreshToken: tokenPayload.refreshToken,
                        // expiresIn is treated as seconds-from-now
                        expiresIn: Date.now() + Number(tokenPayload.expiresIn || 0) * 1000,
                        cognitoId: null,
                        email: contact?.email ?? null,
                        userId: contact?.id ?? null,
                        company: contact?.company
                            ? {
                                name: contact.company.name,
                                companyEmail: contact.company.companyEmail,
                            }
                            : null,
                        firstName: contact?.firstName ?? null,
                        lastName: contact?.lastName ?? null,
                        fullName:
                            contact?.fullName ??
                            (() => {
                                const composed = `${contact?.firstName ?? ""} ${contact?.lastName ?? ""}`.trim();
                                return composed || null;
                            })(),
                        organizationId: contact?.primaryOrganization?.id ?? null,
                        domain,
                    };

                    // As a fallback (or when account is not returned), try to hydrate user from /portal/me
                    try {
                        if (process.env.REACT_APP_API_URL && tokenPayload.accessToken) {
                            const meResponse = await fetch(`${process.env.REACT_APP_API_URL}/portal/me`, {
                                headers: {
                                    Authorization: `Bearer ${tokenPayload.accessToken}`,
                                    "ngrok-skip-browser-warning": "69420",
                                },
                            });

                            if (meResponse.ok) {
                                const meJson: any = await meResponse.json();
                                const user = meJson?.data;
                                if (user) {
                                    authData = {
                                        ...authData,
                                        email: user.email ?? authData.email,
                                        userId: user.id ?? authData.userId,
                                        firstName: user.firstName ?? authData.firstName,
                                        lastName: user.lastName ?? authData.lastName,
                                        fullName: user.fullName ?? authData.fullName,
                                        organizationId: user?.primaryOrganization?.id ?? authData.organizationId,
                                    };
                                }
                            }
                        }
                    } catch (e) {
                        console.error("Failed to fetch /portal/me after login:", e);
                    }

                    dispatch(setAuth(authData));
                    // Preserve the domain query param so WebsitePreview loads the correct site
                    const redirectDomain = searchParams.get("domain");
                    navigate(redirectDomain ? `/?domain=${encodeURIComponent(redirectDomain)}` : "/");
                }
            });
        }
    }, []);

    return <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <img src={AppLoader} alt="Loading website definition" width={130} />
    </div>;
};

export default AuthCallback;
